import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { 
  Award, Trophy, Medal, TrendingUp, Phone, Star, Target, Timer
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';

export default function LeaderboardPage() {
  const { api, user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [myKPIs, setMyKPIs] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [leaderboardRes, kpiRes] = await Promise.all([
        api.get('/kpi/leaderboard'),
        api.get('/kpi/my-stats')
      ]);
      setLeaderboard(leaderboardRes.data);
      setMyKPIs(kpiRes.data);
    } catch (error) {
      console.error('Failed to fetch KPI data:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getRankIcon = (index) => {
    if (index === 0) return <Trophy className="w-6 h-6 text-[#FFD700]" />;
    if (index === 1) return <Medal className="w-6 h-6 text-[#C0C0C0]" />;
    if (index === 2) return <Medal className="w-6 h-6 text-[#CD7F32]" />;
    return <span className="w-6 h-6 flex items-center justify-center text-white/50 font-bold">{index + 1}</span>;
  };

  const getRankClass = (index) => {
    if (index === 0) return 'gold';
    if (index === 1) return 'silver';
    if (index === 2) return 'bronze';
    return '';
  };

  const formatAHT = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="leaderboard-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Performance <span className="text-[#D4AF37]">Leaderboard</span>
          </h1>
          <p className="text-white/50 mt-1">Top performers this month</p>
        </div>

        {/* My KPIs */}
        <Card className="glass-card border-0 border-l-4 border-l-[#D4AF37]">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2">
              <Star className="w-5 h-5 text-[#D4AF37]" />
              Your Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {/* Debt Recovery */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-white/50 text-sm">Debt Recovery</span>
                  <span className="text-[#D4AF37] font-mono font-bold">
                    {myKPIs?.debt_recovery_rate?.toFixed(1) || 0}%
                  </span>
                </div>
                <Progress 
                  value={myKPIs?.debt_recovery_rate || 0} 
                  className="h-2 bg-[#1F1B71]"
                />
              </div>

              {/* Quality Score */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-white/50 text-sm">Quality Score</span>
                  <span className="text-[#10B981] font-mono font-bold">
                    {myKPIs?.quality_score?.toFixed(1) || 0}%
                  </span>
                </div>
                <Progress 
                  value={myKPIs?.quality_score || 0} 
                  className="h-2 bg-[#1F1B71]"
                />
              </div>

              {/* AHT */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-white/50 text-sm">Avg Handle Time</span>
                  <span className="text-white font-mono font-bold">
                    {formatAHT(myKPIs?.aht_seconds || 0)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Timer className="w-4 h-4 text-white/30" />
                  <span className="text-white/50 text-xs">Target: 3:00</span>
                </div>
              </div>

              {/* Calls Handled */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-white/50 text-sm">Calls Handled</span>
                  <span className="text-[#4B167A] font-mono font-bold">
                    {myKPIs?.calls_handled || 0}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-white/30" />
                  <span className="text-white/50 text-xs">This month</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* KPI Gauges */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Debt Recovery Gauge */}
          <Card className="glass-card border-0">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="relative w-32 h-32 mx-auto mb-4">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="rgba(31, 27, 113, 0.5)"
                      strokeWidth="12"
                      fill="none"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="#D4AF37"
                      strokeWidth="12"
                      fill="none"
                      strokeDasharray={`${(myKPIs?.debt_recovery_rate || 0) * 3.52} 352`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold text-[#D4AF37] font-mono">
                      {myKPIs?.debt_recovery_rate?.toFixed(0) || 0}%
                    </span>
                  </div>
                </div>
                <h3 className="text-white font-semibold">Debt Recovery</h3>
                <p className="text-white/50 text-sm">Target: 85%</p>
              </div>
            </CardContent>
          </Card>

          {/* Quality Score Gauge */}
          <Card className="glass-card border-0">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="relative w-32 h-32 mx-auto mb-4">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="rgba(31, 27, 113, 0.5)"
                      strokeWidth="12"
                      fill="none"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="#10B981"
                      strokeWidth="12"
                      fill="none"
                      strokeDasharray={`${(myKPIs?.quality_score || 0) * 3.52} 352`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold text-[#10B981] font-mono">
                      {myKPIs?.quality_score?.toFixed(0) || 0}%
                    </span>
                  </div>
                </div>
                <h3 className="text-white font-semibold">Quality Score</h3>
                <p className="text-white/50 text-sm">Target: 90%</p>
              </div>
            </CardContent>
          </Card>

          {/* AHT Gauge */}
          <Card className="glass-card border-0">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="relative w-32 h-32 mx-auto mb-4">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="rgba(31, 27, 113, 0.5)"
                      strokeWidth="12"
                      fill="none"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="#4B167A"
                      strokeWidth="12"
                      fill="none"
                      strokeDasharray={`${Math.min(100, ((180 - (myKPIs?.aht_seconds || 0)) / 180) * 100) * 3.52} 352`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold text-white font-mono">
                      {formatAHT(myKPIs?.aht_seconds || 0)}
                    </span>
                  </div>
                </div>
                <h3 className="text-white font-semibold">Avg Handle Time</h3>
                <p className="text-white/50 text-sm">Target: &lt; 3:00</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Leaderboard */}
        <Card className="glass-card border-0">
          <CardHeader className="border-b border-white/10">
            <CardTitle className="flex items-center gap-2 text-white">
              <Award className="w-5 h-5 text-[#D4AF37]" />
              Top Performers
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {loading ? (
              <div className="text-center py-8 text-white/50">Loading leaderboard...</div>
            ) : leaderboard.length === 0 ? (
              <div className="text-center py-8 text-white/50">
                No performance data available yet
              </div>
            ) : (
              <div className="space-y-3">
                {leaderboard.map((entry, index) => (
                  <div
                    key={entry.user_id}
                    className={`leaderboard-row ${index < 3 ? 'top-3' : ''}`}
                    data-testid={`leaderboard-row-${index}`}
                  >
                    <div className={`leaderboard-rank ${getRankClass(index)}`}>
                      {getRankIcon(index)}
                    </div>
                    
                    <div className="flex-1 ml-4">
                      <p className="font-semibold text-white">
                        {entry.full_name}
                        {entry.user_id === user?.id && (
                          <Badge className="ml-2 bg-[#D4AF37]/20 text-[#D4AF37]">You</Badge>
                        )}
                      </p>
                      <div className="flex items-center gap-4 mt-1 text-sm text-white/50">
                        <span>Quality: {entry.quality_score?.toFixed(0)}%</span>
                        <span>Recovery: {entry.debt_recovery_rate?.toFixed(0)}%</span>
                        <span>Calls: {entry.calls_handled}</span>
                      </div>
                    </div>

                    <div className="text-right">
                      <p className="text-2xl font-bold text-[#D4AF37] font-mono">
                        {entry.quality_score?.toFixed(0)}
                      </p>
                      <p className="text-xs text-white/50">Quality Score</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
